'use client';

import { JSX, useRef, useEffect, MouseEvent } from 'react';

// Components
import { AtClickable } from '@/components/atoms/at-clickable/at-clickable';
import { MlModal } from '../ml-modal/ml-modal';

// Types
import {
  TClickableSize,
  TClickableVariants,
} from '@/components/atoms/at-clickable/at-clickable.types';
import { IMlAbaFormProps } from './ml-aba-form.types';
import { IModalRefProps } from '../ml-modal/ml-modal.types';

// Hooks
import { useCookiesNext } from 'cookies-next';

/**
 * MlAbaForm
 *
 * A modal-based form component designed for gated content downloads or promotions.
 * Automatically opens on mount, traps keyboard focus for accessibility, and sets a cookie when the CTA is clicked.
 *
 */
export const MlAbaForm = ({
  children,
  ctaProps,
}: IMlAbaFormProps): JSX.Element => {
  const { size, outline, variant, label, aria_label } = ctaProps;

  const modalRef = useRef<IModalRefProps | null>(null);

  /**
   * Opens the modal on mount and sets up keyboard focus trapping.
   * Also closes modal on "Escape" key press.
   */
  useEffect(() => {
    modalRef.current?.handleOpen();
    const focusableButtons: HTMLButtonElement[] = modalRef.current?.dialogRef
      ? Array.from(modalRef?.current?.dialogRef?.querySelectorAll('button'))
      : [];

    let firstTabStop = focusableButtons[0];
    let lastTabStop = focusableButtons[focusableButtons.length - 1];
    const handleFocus = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        if (!firstTabStop || !lastTabStop) return;
        if (e.shiftKey) {
          if (document.activeElement === firstTabStop) {
            e.preventDefault();
            (lastTabStop as HTMLElement).focus();
          }
        } else {
          if (document.activeElement === lastTabStop) {
            e.preventDefault();
            (firstTabStop as HTMLElement).focus();
          }
        }
      }

      // Close the modal on ESC
      if (e.key === 'Escape') {
        modalRef.current?.handleClose();
        document.body.style.overflow = '';
      }
    };

    document.addEventListener('keydown', handleFocus);

    return () => {
      document.removeEventListener('keydown', handleFocus);
      document.body.style.overflow = '';
    };
  }, [modalRef]);

  const { setCookie } = useCookiesNext();
  /**
   * Handles CTA button click.
   * - Sets a cookie to flag that the form has been opened
   * - Closes the modal
   *
   */
  const handleClick = (e: MouseEvent) => {
    setCookie('aba-form', 'true');
    modalRef?.current?.handleClose();
  };

  return (
    <div>
      <MlModal ref={modalRef}>
        <div className="custom-scrollbar flex flex-col items-center justify-center overflow-y-scroll portrait:h-343 portrait:lg:h-390 landscape:h-[calc(100dvh_-_200px)] landscape:lg:h-390">
          <div className="mr-4.5 h-full lg:max-w-760">{children}</div>
        </div>
        <div className="flex items-center justify-center pt-8 lg:pt-6.5">
          <AtClickable
            variant={variant as TClickableVariants}
            size={size.toLowerCase() as TClickableSize}
            label={label}
            outline={outline}
            onClick={handleClick}
            ariaLabel={aria_label}
            interactLocation="ml-aba-form"
          />
        </div>
      </MlModal>
    </div>
  );
};
